import { useState, useEffect } from "react";
import { logger } from "@/lib/logger";

interface LikedAgent {
  agentId: string;
  likedAt: number; // timestamp in milliseconds
}

const STORAGE_KEY = "infragents_liked";
const EXPIRY_HOURS = 24;

/**
 * Custom hook to track liked agents in localStorage
 * Implements client-side rate limiting (once per 24 hours per agent)
 */
export function useLikeTracking() {
  const [likedAgents, setLikedAgents] = useState<Map<string, number>>(
    new Map(),
  );

  useEffect(() => {
    // Load from localStorage on mount and clean up expired entries
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const data: LikedAgent[] = JSON.parse(stored);
        const now = Date.now();

        // Filter out expired likes (older than 24 hours)
        const validLikes = data.filter((item) => {
          const hoursSince = (now - item.likedAt) / (1000 * 60 * 60);
          return hoursSince < EXPIRY_HOURS;
        });

        const map = new Map(
          validLikes.map((item) => [item.agentId, item.likedAt]),
        );
        setLikedAgents(map);

        // Update localStorage if we removed expired items
        if (validLikes.length !== data.length) {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(validLikes));
          logger.log(
            `🧹 Cleaned up ${data.length - validLikes.length} expired like(s)`,
          );
        }

        logger.log(
          `📊 Loaded ${validLikes.length} liked agent(s) from storage`,
        );
      } catch (error) {
        logger.error("Error loading liked agents from localStorage:", error);
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, []);

  /**
   * Check if user can like a specific agent
   * @param agentId - The agent ID to check
   * @returns Object with allowed status and hours remaining if blocked
   */
  const canLike = (
    agentId: string,
  ): { allowed: boolean; hoursRemaining?: number } => {
    const likedAt = likedAgents.get(agentId);

    if (!likedAt) {
      return { allowed: true };
    }

    const now = Date.now();
    const hoursSince = (now - likedAt) / (1000 * 60 * 60);

    if (hoursSince >= EXPIRY_HOURS) {
      // Expired - user can like again
      return { allowed: true };
    }

    // Still within 24 hour window
    return {
      allowed: false,
      hoursRemaining: Math.ceil(EXPIRY_HOURS - hoursSince),
    };
  };

  /**
   * Record that user has liked an agent
   * @param agentId - The agent ID that was liked
   */
  const recordLike = (agentId: string) => {
    const now = Date.now();
    const newLikedAgents = new Map(likedAgents);
    newLikedAgents.set(agentId, now);
    setLikedAgents(newLikedAgents);

    // Save to localStorage
    const data: LikedAgent[] = Array.from(newLikedAgents.entries()).map(
      ([agentId, likedAt]) => ({ agentId, likedAt }),
    );
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));

    logger.log(`❤️ Recorded like for agent: ${agentId}`);
  };

  /**
   * Check if a specific agent has been liked
   * @param agentId - The agent ID to check
   * @returns boolean indicating if agent is currently liked
   */
  const hasLiked = (agentId: string): boolean => {
    return likedAgents.has(agentId) && !canLike(agentId).allowed;
  };

  return { canLike, recordLike, hasLiked };
}
